import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { useMutation, useQuery } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainer } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import StyledInput from '../../components/inputs/styled-input';
import graphqlClient from '../../logic/graphql/graphql-client';
import { GraphqlOperations } from '../../logic/graphql/operations';
import { Mutation, Query } from '../../logic/graphql/types/types';
import { Paths } from '../../navigation/paths';
import { useUserPhoneNumber } from '../../logic/graphql/hooks';
import { withErrorBoundary } from '@sentry/react';
import { Fallback } from '../../components/utils/error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

const PasswordChange = (): JSX.Element => {
  const intl = useIntl();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [firstToken, setFirstToken] = useState<string | undefined>(undefined);

  const [shouldValidateNewPassword, setShouldValidateNewPassword] = useState<boolean>(false);
  const [smsCode, setSMSCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState<string>('');

  const [passwordError, setPasswordError] = useState<string>('');
  const [newPasswordError, setNewPasswordError] = useState<string>('');
  const [newPasswordConfirmationError, setNewPasswordConfirmationError] = useState<string>('');
  const [smsCodeError, setSMSCodeError] = useState<string>('');

  const { phoneNumber } = useUserPhoneNumber();

  const changePassword = useMutation(
    GraphqlOperations.mutations.update_user_password_with_confirm.mutationString,
  )[1];

  const confirmPassword = useMutation(
    GraphqlOperations.mutations.confirm_password_update.mutationString,
  )[1];

  const result = useQuery<Query>({
    query: GraphqlOperations.queries.getUserAllDetails.queryString,
    requestPolicy: 'cache-first',
  })[0];
  const { data } = result;
  const user = data?.getUser;

  const history = useHistory();
  const { addToast } = useToasts();

  const areNewPasswordFieldsValid = (): boolean => {
    return (
      newPassword.length > 7 &&
      newPassword !== currentPassword &&
      newPassword === newPasswordConfirmation
    );
  };

  useEffect(() => {
    if (shouldValidateNewPassword) {
      if (newPassword.length < 8) {
        setNewPasswordError(
          intl.formatMessage({ id: 'profilePage.settings.passwordChange.errors.passwordLength' }),
        );
      } else if (newPassword === currentPassword) {
        setNewPasswordError(
          intl.formatMessage({ id: 'profilePage.settings.passwordChange.errors.samePassword' }),
        );
      } else {
        setNewPasswordError('');
      }

      if (newPassword !== newPasswordConfirmation) {
        setNewPasswordConfirmationError(
          intl.formatMessage({
            id: 'profilePage.settings.passwordChange.errors.passwordsNotMatch',
          }),
        );
      } else {
        setNewPasswordConfirmationError('');
      }
    }
  }, [currentPassword, newPassword, newPasswordConfirmation, shouldValidateNewPassword]);

  const handleNext1Click = async (): Promise<void> => {
    try {
      const result = await graphqlClient
        .query(
          GraphqlOperations.queries.validate_password.queryString,
          GraphqlOperations.queries.validate_password.createVars(currentPassword),
          { requestPolicy: 'network-only' },
        )
        .toPromise();
      if (!result.error && (result.data as Query).validate_password) {
        setCurrentStep(1);
      } else {
        setPasswordError(
          intl.formatMessage({ id: 'profilePage.settings.passwordChange.errors.invalidPassword' }),
        );
      }
    } catch (err) {
      setPasswordError(
        intl.formatMessage({ id: 'profilePage.settings.passwordChange.errors.errorCheckOccurred' }),
      );
    }
  };

  const handleNext2Click = (): void => {
    if (areNewPasswordFieldsValid()) {
      const variables = GraphqlOperations.mutations.update_user_password_with_confirm.createVars(
        currentPassword,
        newPassword,
      );
      changePassword(variables)
        .then((result) => {
          if (result && !result.error) {
            setFirstToken((result.data as Mutation)?.update_user_password_with_confirm?.token);
            setCurrentStep(2);
          } else if (result.error && result.error.message === '[GraphQL] duplicate_new_password') {
            setNewPasswordError(
              intl.formatMessage({
                id: 'profilePage.settings.passwordChange.errors.sameContract',
              }),
            );
          } else {
            setNewPasswordError(
              intl.formatMessage({
                id: 'profilePage.settings.passwordChange.errors.invalidPassword',
              }),
            );
          }
          return true;
        })
        .catch(() =>
          setNewPasswordError(
            intl.formatMessage({
              id: 'profilePage.settings.passwordChange.errors.generalError',
            }),
          ),
        );
    } else {
      setShouldValidateNewPassword(true);
    }
  };

  const handleNext3Click = (): void => {
    if (!firstToken) {
      setCurrentStep(0);
      return;
    }
    const variables = GraphqlOperations.mutations.confirm_password_update.createVars(
      firstToken,
      smsCode,
    );
    confirmPassword(variables)
      .then((result) => {
        if (result && !result.error) {
          history.push(Paths.login_settings);
          addToast(
            intl.formatMessage({ id: 'profilePage.settings.passwordChange.toast.success' }),
            {
              appearance: 'success',
              autoDismiss: true,
            },
          );
        } else {
          setSMSCodeError(
            intl.formatMessage({ id: 'profilePage.settings.passwordChange.errors.invalidSmsCode' }),
          );
        }
        return true;
      })
      .catch(() =>
        setSMSCodeError(
          intl.formatMessage({ id: 'profilePage.settings.passwordChange.errors.invalidSmsCode' }),
        ),
      );
  };

  const handleCurrentPasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setCurrentPassword(event.currentTarget.value);
  };

  const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setNewPassword(event.currentTarget.value);
  };

  const handleNewPasswordConfirmationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setNewPasswordConfirmation(event.currentTarget.value);
  };

  const handleSMSChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSMSCode(event.currentTarget.value);
  };

  const step1Content = (
    <>
      <h2 className="mb-2">
        <FormattedMessage id="profilePage.settings.passwordChange.currentStep.title" />
      </h2>
      <p className="text-14 mb-3">
        <FormattedMessage id="profilePage.settings.passwordChange.currentStep.description" />
      </p>
      <StyledInput
        key="password"
        type="password"
        placeholder={intl.formatMessage({
          id: 'profilePage.settings.passwordChange.currentStep.currentPasswordLabel',
        })}
        onChange={handleCurrentPasswordChange}
        error={passwordError}
        onEnterPress={handleNext1Click}
        autoComplete="current-password"
        autoFocus
      />
      <Button inactive={currentPassword.length < 8} onClick={handleNext1Click}>
        <FormattedMessage id="profilePage.settings.passwordChange.nextButton" />
      </Button>
    </>
  );

  const step2Content = (
    <>
      <h2 className="mb-2">
        <FormattedMessage id="profilePage.settings.passwordChange.newStep.title" />
      </h2>
      <p className="text-14 mb-3">
        <FormattedMessage id="profilePage.settings.passwordChange.newStep.description" />
      </p>
      <StyledInput
        id="email"
        key="email"
        value={user?.email}
        disabled
        blurred
        type="email"
        style={{ display: 'none' }}
      />
      <StyledInput
        key="newpassword"
        type="password"
        placeholder={intl.formatMessage({
          id: 'profilePage.settings.passwordChange.newStep.newPasswordLabel',
        })}
        onChange={handleNewPasswordChange}
        error={newPasswordError}
        onEnterPress={handleNext2Click}
        autoComplete="new-password"
        autoFocus
      />
      <StyledInput
        key="newpasswordconfirmation"
        type="password"
        placeholder={intl.formatMessage({
          id: 'profilePage.settings.passwordChange.newStep.passwordConfirmLabel',
        })}
        onChange={handleNewPasswordConfirmationChange}
        error={newPasswordConfirmationError}
        onEnterPress={handleNext2Click}
        autoComplete="new-password"
      />
      <Button onClick={handleNext2Click}>
        <FormattedMessage id="profilePage.settings.passwordChange.modifyButton" />
      </Button>
    </>
  );

  // új kódot kérek ?
  const step3Content = (
    <>
      <h2 className="mb-2">
        <FormattedMessage id="profilePage.settings.passwordChange.smsStep.title" />
      </h2>
      <p className="text-14 mb-3">
        <FormattedMessage id="profilePage.settings.passwordChange.smsStep.description_a" />
        <b>{phoneNumber}</b>
        <FormattedMessage id="profilePage.settings.passwordChange.smsStep.description_b" />
      </p>
      <StyledInput
        key="smspassword"
        type="text"
        autoComplete="one-time-code"
        inputMode="numeric"
        placeholder={intl.formatMessage({
          id: 'profilePage.settings.passwordChange.smsStep.smsCodeLabel',
        })}
        onChange={handleSMSChange}
        error={smsCodeError}
        onEnterPress={handleNext3Click}
        autoFocus
      />
      <Button onClick={handleNext3Click}>
        <FormattedMessage id="profilePage.settings.passwordChange.confirmButton" />
      </Button>
    </>
  );

  const renderCurrentStepContent = (): JSX.Element | null => {
    switch (currentStep) {
      case 0:
        return step1Content;
      case 1:
        return step2Content;
      case 2:
        return step3Content;
      default:
        return null;
    }
  };

  return (
    <DefaultScreen
      background={assets.images.profile_bg}
      title="Jelszó módosítás"
      backUrl={Paths.login_settings}
      steps={[
        intl.formatMessage({ id: 'profilePage.settings.passwordChange.currentStep.title' }),
        intl.formatMessage({ id: 'profilePage.settings.passwordChange.newStep.title' }),
        intl.formatMessage({ id: 'profilePage.settings.passwordChange.smsStep.title' }),
      ]}
      currentStep={currentStep}
    >
      <BlurredContainer>{renderCurrentStepContent()}</BlurredContainer>
    </DefaultScreen>
  );
};

export default withErrorBoundary(PasswordChange, { fallback: Fallback });
